import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  Circle,
  AlertTriangle,
  ExternalLink,
  FileText,
  ShieldCheck,
} from "lucide-react";
import FileUploader from "@/components/FileUploader";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const DEFAULT_DOC_TYPES = [
  "Audit Report",
  "Balance Sheet",
  "Income & Expenditure Statement",
  "Receipts & Payments Account",
  "Income Tax Return",
  "Form 10B",
  "Investment Statement",
  "Bank Statement",
];
const STATUSES = ["Draft", "Pending", "Approved", "Rejected"];

const emptyForm = {
  financial_year_id: "",
  doc_type: "Audit Report",
  title: "",
  status: "Draft",
  remarks: "",
  file_url: "",
  file_public_id: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

const StatusPill = ({ s }) => {
  const map = {
    Draft: "bg-[#6b655b]/10 text-[#6b655b] border-[#6b655b]/30",
    Pending: "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/40",
    Approved: "bg-[#3a5a40]/10 text-[#3a5a40] border-[#3a5a40]/30",
    Rejected: "bg-[#9e2a2b]/10 text-[#9e2a2b] border-[#9e2a2b]/30",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${map[s] || ""}`}>
      {s || "—"}
    </span>
  );
};

export default function FinancialDocuments() {
  const [fys, setFys] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docTypes, setDocTypes] = useState(DEFAULT_DOC_TYPES);
  const [completeness, setCompleteness] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeFyId, setActiveFyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/financial-years"),
      api.get("/financial-reports/completeness"),
      api.get("/financial-reports/alerts"),
      api.get("/masters/financial_doc_type"),
    ]);
    setFys(a.data);
    setCompleteness(b.data);
    setAlerts(c.data);
    const masterNames = (d.data || []).map((x) => x.name);
    if (masterNames.length) setDocTypes(masterNames);
    const current = a.data.find((x) => x.is_current) || a.data[0];
    if (current && !activeFyId) setActiveFyId(current.id);
  };

  const fetchDocs = async (fyId) => {
    if (!fyId) return;
    const { data } = await api.get(`/financial-documents?fy_id=${fyId}`);
    setDocs(data);
  };

  useEffect(() => {
    fetchAll().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeFyId) fetchDocs(activeFyId);
  }, [activeFyId]);

  const activeFy = useMemo(() => fys.find((f) => f.id === activeFyId), [fys, activeFyId]);

  const grouped = useMemo(() => {
    const map = {};
    docTypes.forEach((t) => (map[t] = []));
    docs.forEach((d) => {
      (map[d.doc_type] = map[d.doc_type] || []).push(d);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => b.version - a.version));
    return map;
  }, [docs, docTypes]);

  const openCreate = (docType) => {
    setEditing(null);
    setForm({
      ...emptyForm,
      financial_year_id: activeFyId,
      doc_type: docType || docTypes[0] || "Audit Report",
    });
    setOpen(true);
  };

  const openEdit = (doc) => {
    setEditing(doc);
    setForm({ ...emptyForm, ...doc });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.financial_year_id) {
      toast.error("Financial Year is required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/financial-documents/${editing.id}`, form);
        setDocs((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Document updated");
      } else {
        const { data } = await api.post("/financial-documents", form);
        setDocs((arr) => [data, ...arr]);
        toast.success(`Document created as version ${data.version}`);
      }
      setOpen(false);
      // refresh reports
      const [b, c] = await Promise.all([
        api.get("/financial-reports/completeness"),
        api.get("/financial-reports/alerts"),
      ]);
      setCompleteness(b.data);
      setAlerts(c.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setApproval = async (doc, status) => {
    try {
      const { data } = await api.post(`/financial-documents/${doc.id}/approval`, { status });
      setDocs((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      toast.success(`Status set to ${status}`);
      const b = await api.get("/financial-reports/completeness");
      setCompleteness(b.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    }
  };

  const doDelete = async () => {
    const d = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/financial-documents/${d.id}`);
      setDocs((arr) => arr.filter((x) => x.id !== d.id));
      toast.success("Document deleted");
      const b = await api.get("/financial-reports/completeness");
      setCompleteness(b.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="financials-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Treasury & Compliance</div>
        <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Financial Records</h1>
        <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
          Financial-year-wise archive of audit reports, balance sheets, ITRs, Form 10B, bank statements
          and more — with version control and approval workflow.
        </p>
      </div>

      <Tabs defaultValue="documents">
        <TabsList className="bg-transparent p-0 border-b border-[#e5e0d8] rounded-none h-auto w-full justify-start gap-6">
          {[
            { v: "documents", l: "Documents" },
            { v: "compliance", l: "Compliance" },
            { v: "alerts", l: `Alerts${alerts.length ? ` · ${alerts.length}` : ""}` },
          ].map((t) => (
            <TabsTrigger
              key={t.v}
              value={t.v}
              data-testid={`financials-tab-${t.v}`}
              className="data-[state=active]:bg-transparent data-[state=active]:text-[#234333] data-[state=active]:border-b-2 data-[state=active]:border-[#234333] rounded-none px-0 py-3 text-sm font-sans text-[#6b655b] shadow-none"
            >
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Documents */}
        <TabsContent value="documents" className="pt-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
                Financial Year
              </div>
              <select
                data-testid="fy-selector"
                className={inputCls + " max-w-xs"}
                value={activeFyId}
                onChange={(e) => setActiveFyId(e.target.value)}
              >
                {fys.map((fy) => (
                  <option key={fy.id} value={fy.id}>
                    {fy.label} {fy.is_current ? "· Current" : ""}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => openCreate()}
              data-testid="fdoc-add-button"
              disabled={!activeFyId}
              className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm disabled:opacity-60"
            >
              <Plus size={14} strokeWidth={1.5} /> Add Document
            </button>
          </div>

          {loading ? (
            <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
          ) : (
            <div className="space-y-4">
              {docTypes.map((t) => {
                const entries = grouped[t] || [];
                return (
                  <div
                    key={t}
                    className="bg-white border border-[#e5e0d8] rounded-sm"
                    data-testid={`fdoc-group-${t.replace(/[^a-z0-9]/gi, "-")}`}
                  >
                    <div className="flex items-center justify-between px-5 py-3 border-b border-[#e5e0d8]">
                      <div className="flex items-center gap-3">
                        {entries.length > 0 ? (
                          <CheckCircle2 size={16} strokeWidth={1.5} className="text-[#3a5a40]" />
                        ) : (
                          <Circle size={16} strokeWidth={1.5} className="text-[#6b655b]" />
                        )}
                        <span className="font-serif text-lg text-[#1c1a17]">{t}</span>
                        <span className="text-xs text-[#6b655b]">
                          {entries.length} version{entries.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <button
                        onClick={() => openCreate(t)}
                        data-testid={`fdoc-add-for-${t.replace(/[^a-z0-9]/gi, "-")}`}
                        className="text-xs text-[#234333] hover:bg-[#efece6] border border-[#e5e0d8] px-3 py-1.5 rounded-sm"
                      >
                        + Add
                      </button>
                    </div>
                    {entries.length === 0 ? (
                      <div className="px-5 py-4 text-sm text-[#6b655b]">
                        Nothing uploaded for {activeFy?.label || "this FY"} yet.
                      </div>
                    ) : (
                      <ul className="divide-y divide-[#e5e0d8]">
                        {entries.map((d) => (
                          <li
                            key={d.id}
                            className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap"
                            data-testid={`fdoc-row-${d.id}`}
                          >
                            <div className="flex items-center gap-3 flex-wrap min-w-0">
                              <span className="font-mono text-[11px] bg-[#f9f8f6] border border-[#e5e0d8] px-2 py-0.5 rounded-sm text-[#234333]">
                                v{d.version}
                              </span>
                              <div className="min-w-0">
                                <div className="text-sm text-[#1c1a17] truncate">
                                  {d.title || t}
                                </div>
                                <div className="text-[11px] text-[#6b655b]">
                                  Uploaded by {d.uploaded_by || "—"} ·{" "}
                                  {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"}
                                  {d.approved_by ? ` · Approved by ${d.approved_by}` : ""}
                                </div>
                              </div>
                              <StatusPill s={d.status} />
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              {d.file_url && (
                                <a
                                  href={d.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                                >
                                  Open <ExternalLink size={11} strokeWidth={1.5} />
                                </a>
                              )}
                              {d.status !== "Approved" && (
                                <button
                                  onClick={() => setApproval(d, "Approved")}
                                  data-testid={`fdoc-approve-${d.id}`}
                                  className="inline-flex items-center gap-1 text-[#3a5a40] hover:bg-[#3a5a40]/5 px-2 py-1 rounded-sm"
                                >
                                  <ShieldCheck size={13} strokeWidth={1.5} /> Approve
                                </button>
                              )}
                              <button
                                onClick={() => openEdit(d)}
                                data-testid={`fdoc-edit-${d.id}`}
                                className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                              >
                                <Pencil size={13} strokeWidth={1.5} /> Edit
                              </button>
                              <button
                                onClick={() => setConfirmDelete(d)}
                                data-testid={`fdoc-delete-${d.id}`}
                                className="inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                              >
                                <Trash2 size={13} strokeWidth={1.5} />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Compliance */}
        <TabsContent value="compliance" className="pt-8">
          <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
            <table className="w-full text-xs" data-testid="compliance-table">
              <thead>
                <tr className="bg-[#f9f8f6] text-left">
                  <th className="px-3 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold sticky left-0 bg-[#f9f8f6]">
                    FY
                  </th>
                  {docTypes.map((t) => (
                    <th key={t} className="px-2 py-3 text-[9px] tracking-wide uppercase text-[#6b655b] font-semibold text-center">
                      {t.split(" ").slice(0, 2).join(" ")}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                    %
                  </th>
                </tr>
              </thead>
              <tbody>
                {completeness.map((row) => (
                  <tr
                    key={row.fy_id}
                    className={`border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60 cursor-pointer ${row.is_current ? "bg-[#d4af37]/5" : ""}`}
                    onClick={() => {
                      setActiveFyId(row.fy_id);
                      // Switch tab back to Documents
                      const trigger = document.querySelector('[data-testid="financials-tab-documents"]');
                      trigger?.click();
                    }}
                    data-testid={`compliance-row-${row.fy_id}`}
                  >
                    <td className="px-3 py-2 font-serif text-[#1c1a17] sticky left-0 bg-white">
                      {row.label}
                      {row.is_current && (
                        <span className="ml-2 text-[9px] tracking-wider uppercase text-[#8a6d1d]">
                          current
                        </span>
                      )}
                    </td>
                    {row.per_type.map((c) => (
                      <td key={c.doc_type} className="px-2 py-2 text-center">
                        {c.count > 0 ? (
                          <span
                            className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold ${
                              c.latest_status === "Approved"
                                ? "bg-[#3a5a40] text-white"
                                : c.latest_status === "Rejected"
                                  ? "bg-[#9e2a2b] text-white"
                                  : "bg-[#d4af37]/30 text-[#8a6d1d]"
                            }`}
                            title={`${c.count} version(s) · ${c.latest_status || "Draft"}`}
                          >
                            {c.count}
                          </span>
                        ) : (
                          <span className="text-[#6b655b]/40">·</span>
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-2 font-mono text-[#234333]">{row.percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-[11px] text-[#6b655b] flex gap-4 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-[#3a5a40]" /> Approved
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-[#d4af37]/60" /> Draft / Pending
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-[#9e2a2b]" /> Rejected
            </span>
            <span>· Click a row to open that FY's documents.</span>
          </div>
        </TabsContent>

        {/* Alerts */}
        <TabsContent value="alerts" className="pt-8">
          {alerts.length === 0 ? (
            <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center">
              <ShieldCheck size={32} strokeWidth={1.5} className="mx-auto text-[#3a5a40]" />
              <div className="mt-4 font-serif text-2xl text-[#1c1a17]">All clear</div>
              <div className="mt-2 text-sm text-[#6b655b]">
                No overdue financial documents across any financial year.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((a) => (
                <div
                  key={a.fy_id}
                  data-testid={`alert-row-${a.fy_id}`}
                  className={`bg-white border rounded-sm p-5 ${
                    a.severity === "high" ? "border-[#9e2a2b]/40" : "border-[#d4af37]/50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <AlertTriangle
                          size={16}
                          strokeWidth={1.5}
                          className={a.severity === "high" ? "text-[#9e2a2b]" : "text-[#8a6d1d]"}
                        />
                        <div className="font-serif text-xl text-[#1c1a17]">FY {a.label}</div>
                        <div className="text-[11px] text-[#6b655b] tracking-wide uppercase">
                          Deadline passed · {a.deadline}
                        </div>
                      </div>
                      {a.missing.length > 0 && (
                        <div className="mt-2 text-sm text-[#1c1a17]">
                          <span className="text-[#6b655b]">Missing:</span>{" "}
                          {a.missing.map((m) => (
                            <span
                              key={m}
                              className="inline-block text-xs bg-[#9e2a2b]/10 text-[#9e2a2b] border border-[#9e2a2b]/30 px-2 py-0.5 rounded-sm mr-1 mb-1"
                            >
                              {m}
                            </span>
                          ))}
                        </div>
                      )}
                      {a.not_approved.length > 0 && (
                        <div className="mt-1 text-sm text-[#1c1a17]">
                          <span className="text-[#6b655b]">Not approved:</span>{" "}
                          {a.not_approved.map((m) => (
                            <span
                              key={m.doc_type}
                              className="inline-block text-xs bg-[#d4af37]/15 text-[#8a6d1d] border border-[#d4af37]/40 px-2 py-0.5 rounded-sm mr-1 mb-1"
                            >
                              {m.doc_type} ({m.status})
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setActiveFyId(a.fy_id);
                        document.querySelector('[data-testid="financials-tab-documents"]')?.click();
                      }}
                      data-testid={`alert-open-${a.fy_id}`}
                      className="text-xs bg-[#234333] hover:bg-[#1b3326] text-white px-3 py-2 rounded-sm"
                    >
                      Open FY
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="fdoc-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? `Edit — v${editing.version}` : "Add Financial Document"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Financial Year">
              <select
                data-testid="fdoc-fy-select"
                className={inputCls}
                value={form.financial_year_id}
                onChange={(e) => set("financial_year_id", e.target.value)}
                disabled={!!editing}
              >
                {fys.map((fy) => (
                  <option key={fy.id} value={fy.id}>
                    {fy.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Document Type">
              <select
                data-testid="fdoc-type-select"
                className={inputCls}
                value={form.doc_type}
                onChange={(e) => set("doc_type", e.target.value)}
              >
                {docTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                {form.doc_type && !docTypes.includes(form.doc_type) ? (
                  <option value={form.doc_type}>{form.doc_type} (legacy)</option>
                ) : null}
              </select>
            </Field>
            <Field label="Title / Short description" full>
              <input
                data-testid="fdoc-title-input"
                className={inputCls}
                placeholder="e.g., FY 2024-25 Audit Report (Final)"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
              />
            </Field>
            <Field label="Status">
              <select
                data-testid="fdoc-status-select"
                className={inputCls}
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Remarks" full>
              <textarea
                data-testid="fdoc-remarks-input"
                className={inputCls + " min-h-[72px] resize-y"}
                value={form.remarks}
                onChange={(e) => set("remarks", e.target.value)}
              />
            </Field>

            <div className="md:col-span-2 pt-2">
              <FileUploader
                kind="financial_document"
                label="Document File"
                url={form.file_url}
                testid="fdoc-file"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, file_url: url, file_public_id: public_id }))
                }
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="fdoc-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="fdoc-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this document?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.financial_year_label} · {confirmDelete?.doc_type} v
              {confirmDelete?.version}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="fdoc-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
